import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import {
  Send,
  Info,
  Bot,
  User as UserIcon,
  Eye,
  MessageSquare,
  Bell,
  Zap,
  FileText,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Event, Session, User, Notification, AppUser } from '../../types';
import { eventsApi, chatApi, sessionsApi, apiService, notificationsApi, adminApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Prism } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Type assertion to fix TypeScript compatibility issue with react-syntax-highlighter
// This is a known issue with react-syntax-highlighter types and React 18+
const SyntaxHighlighter = Prism as any as React.ComponentType<any>;

interface ChatMessage {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  metadata?: any;
}

// Simple collapsible panel component
const CollapsePanel: React.FC<{
  header: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}> = ({ header, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-md mb-2">
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left text-sm font-medium text-gray-700 rounded-t-md"
        onClick={() => setOpen(o => !o)}
      >
        <span>{header}</span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && <div className="px-4 py-3">{children}</div>}
    </div>
  );
};

// Simple tab component
const TabGroup: React.FC<{
  tabs: { key: string; label: string; content: React.ReactNode }[];
  defaultTab?: string;
}> = ({ tabs, defaultTab }) => {
  const [active, setActive] = useState(defaultTab || tabs[0]?.key);
  return (
    <div>
      <div className="flex border-b border-gray-200 mb-4 gap-1">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActive(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-t-md border border-b-0 ${
              active === tab.key
                ? 'bg-white text-blue-600 border-gray-200 -mb-px'
                : 'bg-gray-50 text-gray-600 border-transparent hover:text-gray-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {tabs.find(t => t.key === active)?.content}
    </div>
  );
};

// Simple modal component
const Modal: React.FC<{
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  wide?: boolean;
}> = ({ open, onClose, title, children, wide }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-5 px-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative bg-white rounded-lg shadow-xl w-full ${wide ? 'max-w-5xl' : 'max-w-lg'} max-h-[90vh] flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 font-semibold text-gray-900">{title}</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ChatTest: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [clients, setClients] = useState<AppUser[]>([]);
  const [loadingClients, setLoadingClients] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [currentSession, setCurrentSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [inspectionVisible, setInspectionVisible] = useState(false);
  const [lastPromptData, setLastPromptData] = useState<any>(null);
  const [conversationStarted, setConversationStarted] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const lastFetchedAtRef = useRef<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // RAG Chat state - Default to TRUE for better answers
  const [useRAG, setUseRAG] = useState(true);
  const [ragTopK, setRagTopK] = useState(5);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isAdmin && clients.length === 0) {
      fetchClients();
    }
    fetchEvents();
    fetchUsers();
  }, [isAdmin]);

  // Fetch events when client filter changes (for admins)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isAdmin) {
      fetchEvents();
      // Reset event selection when client changes
      if (selectedEvent) {
        setSelectedEvent('');
        setMessages([]);
        setCurrentSession(null);
        setLastPromptData(null);
        setConversationStarted(false);
      }
    }
  }, [selectedClientId]);

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const response = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      if (response.data.success && response.data.data) {
        const users = response.data.data.users || [];
        setClients(users);
      }
    } catch (error: any) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  // Auto-enable conversation when RAG mode is enabled and event is selected
  useEffect(() => {
    if (useRAG && selectedEvent && messages.length === 0) {
      setConversationStarted(true);

      // Add welcome message for RAG mode
      const welcomeMessage: ChatMessage = {
        id: 'rag-welcome',
        type: 'bot',
        content: `👋 **Welcome to RAG Mode!**

I'll search through the document chunks and answer your questions based on the most relevant content.

🔍 **How it works:**
- I retrieve the top ${ragTopK} most relevant chunks from your documents
- Only the relevant parts are sent to the AI (not full documents)
- This makes responses faster and more focused

💡 **Try asking:**
- "What is this project about?"
- "Explain the main features"
- "What problem does it solve?"

Go ahead, ask me anything about your documents!`,
        timestamp: new Date(),
      };

      setMessages([welcomeMessage]);
    }
  }, [useRAG, selectedEvent, ragTopK]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchEvents = async () => {
    try {
      const params: any = { isActive: true, limit: 100 };

      // For admins, filter events by selected client
      if (isAdmin && selectedClientId) {
        params.clientId = selectedClientId;
      } else if (!isAdmin && user?._id) {
        // Clients automatically see only their events
        params.clientId = user._id;
      }
      // If admin and no client selected, show all events

      const response = await eventsApi.getAll(params);
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiService.get('/users');
      if (response.success && response.data) {
        setUsers(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleEventChange = async (eventId: string) => {
    setSelectedEvent(eventId);
    setMessages([]);
    setCurrentSession(null);
    setLastPromptData(null);
    setConversationStarted(false);
    // notifications reset and initial fetch
    setNotifications([]);
    setUnreadCount(0);
    lastFetchedAtRef.current = null;
    if (pollingRef.current) clearInterval(pollingRef.current as any);
    if (eventId) {
      await fetchNotifications(eventId);
      startNotificationPolling(eventId);
    }
  };

  const handleUserChange = (userId: string) => {
    setSelectedUser(userId);
    setMessages([]);
    setCurrentSession(null);
    setLastPromptData(null);
    setConversationStarted(false);
  };

  const startNotificationPolling = (eventId: string) => {
    if (pollingRef.current) clearInterval(pollingRef.current as any);
    pollingRef.current = setInterval(() => {
      fetchNotifications(eventId);
    }, 30000);
  };

  const fetchNotifications = async (eventId: string) => {
    try {
      const since = lastFetchedAtRef.current || undefined;
      const resp = await notificationsApi.listForEvent(eventId, { since });
      const list = resp.data.data?.notifications || [];
      if (list.length) {
        setNotifications(prev => {
          const existingIds = new Set(prev.map(n => n._id));
          const newOnes = list.filter(n => !existingIds.has(n._id));
          const merged = [...newOnes, ...prev];
          if (newOnes.length) setUnreadCount(count => count + newOnes.length);
          return merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        });
      }
      lastFetchedAtRef.current = new Date().toISOString();
    } catch (e) {
      console.error('Failed to fetch notifications', e);
    }
  };

  const openNotifications = async () => {
    setShowNotifications(true);
    try {
      const unseen = notifications;
      await Promise.all(unseen.map(n => notificationsApi.markSeen(n._id, { userId: selectedUser || undefined } as any).catch(() => null)));
      setUnreadCount(0);
    } catch (e) {
      // ignore
    }
  };

  const startConversation = async () => {
    if (!selectedEvent || !selectedUser) return;

    setLoading(true);
    try {
      const selectedUserData = users.find(u => u.userId === selectedUser);
      if (!selectedUserData) {
        console.error('Selected user not found');
        return;
      }

      // Send "start" message to initialize the session
      const chatRequest = {
        userId: selectedUser,
        eventId: selectedEvent,
        message: 'start',
        sessionId: undefined, // Let backend create new session
      };

      const response = await chatApi.sendMessage(chatRequest);

      if (response.data.success && response.data.data) {
        const chatData = response.data.data;

        const welcomeMessage: ChatMessage = {
          id: Date.now().toString(),
          type: 'bot',
          content: chatData.response,
          timestamp: new Date(),
          metadata: chatData.metadata,
        };

        setMessages([welcomeMessage]);

        // Create a session object from the response
        if (chatData.sessionId) {
          const sessionData: Session = {
            sessionId: chatData.sessionId,
            userId: selectedUser,
            eventId: selectedEvent,
            userData: users.find(u => u.userId === selectedUser)!,
            documentData: [],
            guardrails: [],
            behavioralPrompts: [],
            conversations: [],
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          setCurrentSession(sessionData);

          // Fetch real event data for prompt inspection
          fetchEventDataForPromptInspection('start', chatData.response, chatData.metadata);
        }
        setConversationStarted(true);
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !selectedEvent || !selectedUser) return;

    // Use RAG chat if enabled
    if (useRAG) {
      await handleSendRAGMessage();
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const chatRequest = {
        userId: selectedUser,
        eventId: selectedEvent,
        message: inputMessage,
        sessionId: currentSession?.sessionId,
      };

      const response = await chatApi.sendMessage(chatRequest);
      const chatResponse = response.data.data;

      if (chatResponse) {
        // Get updated session data for prompt inspection
        const sessionResponse = await sessionsApi.getById(chatResponse.sessionId);
        const sessionData = sessionResponse.data.data;

        setCurrentSession(sessionData || null);

        // Fetch real event data for prompt inspection
        fetchEventDataForPromptInspection(inputMessage, chatResponse.response, response.data.data?.metadata || {});

        const botMessage: ChatMessage = {
          id: chatResponse.conversationId,
          type: 'bot',
          content: chatResponse.response,
          timestamp: new Date(),
          metadata: chatResponse.metadata,
        };

        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'bot',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRAGMessage = async () => {
    if (!inputMessage.trim() || !selectedEvent) {
      console.log('Missing required fields for RAG:', { inputMessage: !!inputMessage.trim(), selectedEvent: !!selectedEvent });
      return;
    }

    console.log('🚀 Sending RAG message:', { query: inputMessage, eventId: selectedEvent, topK: ragTopK });

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const queryText = inputMessage;
    setInputMessage('');
    setLoading(true);

    try {
      const ragRequest = {
        query: queryText,
        eventId: selectedEvent,
        topK: ragTopK,
        userId: selectedUser,
        sessionId: currentSession?.sessionId,
      };

      console.log('📤 RAG Request:', ragRequest);
      const response = await apiService.post('/chat/rag', ragRequest);
      console.log('📥 RAG Response:', response);

      if (response.success && response.data) {
        const ragData = response.data;

        // Store RAG-specific data for inspection
        setLastPromptData({
          userMessage: queryText,
          sessionData: null, // RAG doesn't use session
          response: ragData.response,
          metadata: {
            chunksUsed: ragData.chunksUsed,
            promptSent: ragData.promptSent,
            model: ragData.model,
            tokensUsed: ragData.tokensUsed,
            processingTime: ragData.processingTime,
          },
          isRAG: true,
        });

        const botMessage: ChatMessage = {
          id: Date.now().toString(),
          type: 'bot',
          content: ragData.response,
          timestamp: new Date(),
          metadata: {
            chunksUsed: ragData.chunksUsed,
            tokensUsed: ragData.tokensUsed,
            processingTime: ragData.processingTime,
          },
        };

        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error: any) {
      console.error('Error sending RAG message:', error);
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'bot',
        content: `Sorry, I encountered an error with RAG chat: ${error.response?.data?.message || error.message || 'Unknown error'}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const fetchEventDataForPromptInspection = async (userMessage: string, response: string, metadata: any) => {
    try {
      if (!selectedEvent) return;

      // Fetch guardrails, behavioral prompts, and documents for the selected event
      const [guardrailsResponse, promptsResponse, documentsResponse] = await Promise.all([
        apiService.get(`/guardrails/event/${selectedEvent}`),
        apiService.get(`/behavioral-prompts/event/${selectedEvent}`),
        apiService.get(`/documents/event/${selectedEvent}`)
      ]);

      const sessionData: Session = {
        sessionId: currentSession?.sessionId || 'temp',
        userId: selectedUser,
        eventId: selectedEvent,
        userData: users.find(u => u.userId === selectedUser)!,
        documentData: documentsResponse.success ? documentsResponse.data || [] : [],
        guardrails: guardrailsResponse.success ? guardrailsResponse.data || [] : [],
        behavioralPrompts: promptsResponse.success ? promptsResponse.data || [] : [],
        conversations: currentSession?.conversations || [],
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setLastPromptData({
        userMessage,
        sessionData,
        response,
        metadata
      });
    } catch (error) {
      console.error('Error fetching event data for prompt inspection:', error);
      // Fallback with empty data
      setLastPromptData({
        userMessage,
        sessionData: null,
        response,
        metadata
      });
    }
  };

  const clearChat = () => {
    setMessages([]);
    setCurrentSession(null);
    setLastPromptData(null);
  };

  const renderPromptInspection = () => {
    if (!lastPromptData) return null;

    const { sessionData, userMessage, response, metadata, isRAG } = lastPromptData;

    // RAG mode inspection
    if (isRAG) {
      return (
        <TabGroup
          defaultTab="prompt"
          tabs={[
            {
              key: 'prompt',
              label: 'RAG Prompt Sent',
              content: (
                <div>
                  <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md mb-4 text-sm text-blue-800">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" />
                    This is the actual prompt sent to the AI with retrieved document chunks
                  </div>
                  <SyntaxHighlighter
                    language="text"
                    style={tomorrow}
                    customStyle={{ fontSize: '12px', maxHeight: '500px' }}
                  >
                    {metadata.promptSent || 'Prompt not available'}
                  </SyntaxHighlighter>
                </div>
              ),
            },
            {
              key: 'chunks',
              label: `Chunks Used (${metadata.chunksUsed?.length || 0})`,
              content: (
                <div>
                  <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md mb-4 text-sm text-green-800">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" />
                    Retrieved {metadata.chunksUsed?.length || 0} most relevant chunks from documents
                  </div>
                  {metadata.chunksUsed && metadata.chunksUsed.length > 0 ? (
                    <div>
                      {metadata.chunksUsed.map((chunk: any, idx: number) => (
                        <CollapsePanel
                          key={idx}
                          header={
                            <span className="flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              <span className="font-medium">Chunk {idx + 1}</span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                Similarity: {(chunk.similarity * 100).toFixed(2)}%
                              </span>
                            </span>
                          }
                        >
                          <table className="w-full border border-gray-200 rounded text-sm mb-3">
                            <tbody>
                              <tr className="border-b border-gray-200">
                                <td className="px-3 py-2 font-medium bg-gray-50 w-36">Document ID</td>
                                <td className="px-3 py-2">{chunk.documentId}</td>
                              </tr>
                              <tr className="border-b border-gray-200">
                                <td className="px-3 py-2 font-medium bg-gray-50">Chunk Index</td>
                                <td className="px-3 py-2">{chunk.chunkIndex}</td>
                              </tr>
                              <tr>
                                <td className="px-3 py-2 font-medium bg-gray-50">Similarity Score</td>
                                <td className="px-3 py-2">{chunk.similarity?.toFixed(4)}</td>
                              </tr>
                            </tbody>
                          </table>
                          <p className="text-sm font-medium mb-1">Content:</p>
                          <div className="bg-gray-100 p-3 rounded text-xs font-mono whitespace-pre-wrap max-h-72 overflow-y-auto">
                            {chunk.content}
                          </div>
                        </CollapsePanel>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                      <Info className="w-4 h-4 mt-0.5 shrink-0" />
                      No chunks were used (no relevant documents found)
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: 'metadata',
              label: 'Response Metadata',
              content: (
                <div>
                  <table className="w-full border border-gray-200 rounded text-sm mb-4">
                    <tbody>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 font-medium bg-gray-50 w-40">Model</td>
                        <td className="px-3 py-2">{metadata.model}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 font-medium bg-gray-50">Tokens Used</td>
                        <td className="px-3 py-2">{metadata.tokensUsed || 'N/A'}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 font-medium bg-gray-50">Processing Time</td>
                        <td className="px-3 py-2">{metadata.processingTime}ms</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-medium bg-gray-50">Chunks Retrieved</td>
                        <td className="px-3 py-2">{metadata.chunksUsed?.length || 0}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="text-sm font-medium mb-2">Full Response:</p>
                  <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px', marginTop: '8px' }}>
                    {JSON.stringify(response, null, 2)}
                  </SyntaxHighlighter>
                </div>
              ),
            },
          ]}
        />
      );
    }

    // Regular chat mode inspection
    const constructedPrompt = buildPromptString(sessionData || null, userMessage || 'No message available');

    return (
      <TabGroup
        defaultTab="prompt"
        tabs={[
          {
            key: 'prompt',
            label: 'Generated Prompt',
            content: (
              <SyntaxHighlighter
                language="text"
                style={tomorrow}
                customStyle={{ fontSize: '12px', maxHeight: '400px' }}
              >
                {constructedPrompt}
              </SyntaxHighlighter>
            ),
          },
          {
            key: 'session',
            label: 'Session Data',
            content: (
              <div>
                <CollapsePanel header="User Data">
                  <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px' }}>
                    {JSON.stringify(sessionData.userData, null, 2)}
                  </SyntaxHighlighter>
                </CollapsePanel>
                <CollapsePanel header={`Guardrails (${sessionData.guardrails.length})`}>
                  <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px' }}>
                    {JSON.stringify(sessionData.guardrails, null, 2)}
                  </SyntaxHighlighter>
                </CollapsePanel>
                <CollapsePanel header={`Behavioral Prompts (${sessionData.behavioralPrompts.length})`}>
                  <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px' }}>
                    {JSON.stringify(sessionData.behavioralPrompts, null, 2)}
                  </SyntaxHighlighter>
                </CollapsePanel>
                <CollapsePanel header={`Documents (${sessionData.documentData.length})`}>
                  <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px' }}>
                    {JSON.stringify(sessionData.documentData.map((doc: any) => ({
                      fileName: doc.originalName,
                      fileType: doc.fileType,
                      summary: doc.summary,
                      keywords: doc.keywords,
                      textLength: doc.extractedText.length,
                    })), null, 2)}
                  </SyntaxHighlighter>
                </CollapsePanel>
                <CollapsePanel header={`Conversation History (${sessionData.conversations.length})`}>
                  <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px' }}>
                    {JSON.stringify(sessionData.conversations, null, 2)}
                  </SyntaxHighlighter>
                </CollapsePanel>
              </div>
            ),
          },
          {
            key: 'metadata',
            label: 'Response Metadata',
            content: (
              <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px' }}>
                {JSON.stringify(response, null, 2)}
              </SyntaxHighlighter>
            ),
          },
        ]}
      />
    );
  };

  const buildPromptString = (sessionData: Session | null, userMessage: string) => {
    if (!sessionData) {
      return `[Session data not available for prompt reconstruction]

User Message: "${userMessage}"

Note: This is a simulated prompt reconstruction. The actual backend uses real session data including:
- Guardrails for content validation
- Behavioral prompts for response style
- Document context from uploaded files
- Conversation history for context`;
    }

    const { guardrails, behavioralPrompts, documentData, conversations } = sessionData;
    const detectedLanguage = 'auto-detected'; // This would be the actual detected language

    const relevantPrompts = behavioralPrompts
      .filter(p => p.isActive)
      .sort((a, b) => b.priority - a.priority);

    const documentContext = documentData
      .filter(d => d.isProcessed)
      .map(d => `Document: ${d.originalName}\nContent: ${d.extractedText.substring(0, 2000)}...`)
      .join('\n\n');

    const conversationHistory = conversations
      .slice(-10)
      .map(c => `User: ${c.userMessage}\nBot: ${c.botResponse}`)
      .join('\n\n');

    const activeGuardrails = guardrails
      .filter(g => g.isActive)
      .sort((a, b) => b.priority - a.priority)
      .map(g => g.rules)
      .flat();

    return `You are a helpful, friendly, and knowledgeable chatbot for a course event. You must respond in ${detectedLanguage} language.

BEHAVIORAL GUIDELINES:
${relevantPrompts.map(p => `- ${p.prompt}`).join('\n')}

GUARDRAILS (MUST FOLLOW):
${activeGuardrails.map((rule, index) => `${index + 1}. ${rule}`).join('\n')}

DOCUMENT CONTEXT:
${documentContext}

CONVERSATION HISTORY:
${conversationHistory}

Current user message: "${userMessage}"

Instructions:
1. Respond only to topics related to the course documents provided
2. Be friendly, helpful, and engaging
3. If the question is not related to the course content, politely redirect to course-related topics
4. Use the conversation history for context but focus on the current question
5. Respond in ${detectedLanguage} language
6. Follow all guardrails strictly
7. Keep responses concise but informative

Response:`;
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Bot className="w-7 h-7" />
          Chatbot Testing Interface
        </h2>
        <p className="text-gray-500 mt-1">
          Test the chatbot functionality with real session data and inspect the AI prompts
        </p>
      </div>

      {/* Configuration */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-base font-semibold text-gray-800 mb-4">Test Configuration</h3>
        <div className="flex flex-wrap gap-4 items-end">
          {isAdmin && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Client</label>
              <select
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-72"
                value={selectedClientId}
                disabled={loadingClients}
                onChange={e => {
                  setSelectedClientId(e.target.value);
                  setSelectedEvent('');
                  setMessages([]);
                  setCurrentSession(null);
                  setLastPromptData(null);
                  setConversationStarted(false);
                }}
              >
                <option value="">All Clients</option>
                {clients.map(client => (
                  <option key={client._id} value={client._id}>
                    {client.name} ({client.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Knowledge Assistant</label>
            <select
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-72"
              value={selectedEvent}
              onChange={e => handleEventChange(e.target.value)}
            >
              <option value="">Select a Knowledge Assistant to test</option>
              {events.map(event => (
                <option key={event._id} value={event._id}>
                  {event.name} ({event.eventId})
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">User</label>
            <select
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-72"
              value={selectedUser}
              onChange={e => handleUserChange(e.target.value)}
            >
              <option value="">Select a user to test</option>
              {users.map(u => (
                <option key={u.userId} value={u.userId}>
                  {u.name} ({u.email}){u.occupation ? ` - ${u.occupation}` : ''}
                </option>
              ))}
            </select>
          </div>

          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={startConversation}
            disabled={!selectedEvent || !selectedUser || conversationStarted || loading || useRAG}
          >
            {loading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {conversationStarted ? 'Conversation Started' : 'Start Conversation'}
          </button>

          <button
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={clearChat}
            disabled={!messages.length}
          >
            Clear Chat
          </button>

          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={() => setInspectionVisible(true)}
            disabled={!lastPromptData}
          >
            <Info className="w-4 h-4" />
            Inspect Last Prompt
          </button>
        </div>

        <hr className="border-gray-200 my-4" />

        {/* RAG Mode Configuration */}
        <div
          className={`flex items-start gap-3 p-3 rounded-md border mb-4 ${
            useRAG
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}
        >
          <Zap className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">RAG Mode (Retrieval Augmented Generation)</p>
            <p className="text-sm mt-0.5">
              {useRAG
                ? 'RAG mode enabled: Only relevant document chunks will be sent to AI (no session, guardrails, or behavioral prompts)'
                : 'Regular chat mode: Uses full documents, session history, guardrails, and behavioral prompts'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Enable RAG Mode:</span>
            <button
              role="switch"
              aria-checked={useRAG}
              onClick={() => {
                setUseRAG(!useRAG);
                setMessages([]);
                setConversationStarted(false);
                setLastPromptData(null);
                if (useRAG) {
                  setCurrentSession(null);
                }
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                useRAG ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  useRAG ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                useRAG ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
              }`}
            >
              {useRAG ? 'RAG Active' : 'Regular Chat'}
            </span>
          </div>

          {useRAG && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Top K Chunks:</span>
              <input
                type="number"
                min={1}
                max={20}
                value={ragTopK}
                onChange={e => setRagTopK(Number(e.target.value) || 5)}
                className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-500">(Number of chunks to retrieve)</span>
            </div>
          )}
        </div>
      </div>

      {/* Chat Interface */}
      <div className="flex gap-4" style={{ height: '600px' }}>
        {/* Chat Messages */}
        <div className="flex-1 flex flex-col bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Chat header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50">
            <MessageSquare className="w-4 h-4 text-gray-600" />
            <span className="font-medium text-gray-800">Chat Session</span>
            <div className="relative">
              <button
                onClick={openNotifications}
                className="p-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-100 text-sm flex items-center gap-1"
              >
                <Bell className="w-3.5 h-3.5" />
              </button>
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-xs font-bold">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            {currentSession && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                Session: {currentSession.sessionId.substring(0, 8)}...
              </span>
            )}
          </div>

          {/* Alerts */}
          {((!selectedEvent || (!selectedUser && !useRAG))) && (
            <div className="flex items-start gap-2 m-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              {!selectedEvent && !selectedUser
                ? useRAG
                  ? 'Please select a Knowledge Assistant to start RAG testing'
                  : 'Please select both a Knowledge Assistant and a user to start testing'
                : !selectedEvent
                  ? 'Please select a Knowledge Assistant to start testing'
                  : !useRAG
                    ? 'Please select a user to start testing'
                    : ''}
            </div>
          )}

          {(!conversationStarted && selectedEvent && selectedUser && !useRAG) && (
            <div className="flex items-center justify-between m-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 shrink-0" />
                Click 'Start Conversation' to begin the chat session
              </div>
              <button
                className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 flex items-center gap-1"
                onClick={startConversation}
                disabled={loading}
              >
                {loading && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Start Conversation
              </button>
            </div>
          )}

          {(selectedEvent && (useRAG || (selectedUser && conversationStarted))) && (
            <>
              {/* Messages Container */}
              <div
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto p-4 bg-gray-50"
              >
                {messages.length === 0 && (
                  <div className="flex flex-col items-center justify-center mt-16 text-gray-400">
                    <Bot className="w-12 h-12 mb-4" />
                    <p>Start a conversation to test the chatbot</p>
                  </div>
                )}

                <div className="space-y-2">
                  {messages.map(message => (
                    <div
                      key={message.id}
                      className={`flex w-full ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`flex gap-2 items-start max-w-[70%] ${
                          message.type === 'user' ? 'flex-row-reverse' : 'flex-row'
                        }`}
                      >
                        {message.type === 'bot' && (
                          <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                            <Bot className="w-4 h-4 text-white" />
                          </div>
                        )}

                        <div
                          className={`px-4 py-3 rounded-xl text-sm ${
                            message.type === 'user'
                              ? 'bg-blue-500 text-white whitespace-pre-wrap'
                              : 'bg-white text-gray-900 border border-gray-100'
                          }`}
                        >
                          {message.type === 'bot' ? (
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                h1: ({children}) => <h1 className="text-lg font-bold mb-2 text-blue-600">{children}</h1>,
                                h2: ({children}) => <h2 className="text-base font-bold mb-1.5 text-blue-600">{children}</h2>,
                                h3: ({children}) => <h3 className="text-sm font-bold mb-1 text-blue-600">{children}</h3>,
                                p: ({children}) => <p className="mb-2 leading-relaxed">{children}</p>,
                                ul: ({children}) => <ul className="mb-2 pl-5 list-disc">{children}</ul>,
                                ol: ({children}) => <ol className="mb-2 pl-5 list-decimal">{children}</ol>,
                                li: ({children}) => <li className="mb-1">{children}</li>,
                                strong: ({children}) => <strong className="font-bold text-blue-600">{children}</strong>,
                                em: ({children}) => <em className="italic text-purple-600">{children}</em>,
                                code: ({children}) => <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                                blockquote: ({children}) => <blockquote className="border-l-4 border-blue-400 pl-3 my-2 italic">{children}</blockquote>,
                                table: ({children}) => <table className="w-full border-collapse mb-2">{children}</table>,
                                th: ({children}) => <th className="border border-gray-300 px-2 py-1 bg-gray-50 font-bold text-left">{children}</th>,
                                td: ({children}) => <td className="border border-gray-300 px-2 py-1">{children}</td>,
                              }}
                            >
                              {message.content}
                            </ReactMarkdown>
                          ) : (
                            message.content
                          )}
                        </div>

                        {message.type === 'user' && (
                          <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                            <UserIcon className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {loading && (
                  <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500">
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    Assistant is typing...
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="p-4 border-t border-gray-200 flex gap-2">
                <textarea
                  rows={2}
                  value={inputMessage}
                  onChange={e => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    useRAG
                      ? 'Ask a question about the documents...'
                      : 'Type your message here... (Press Enter to send, Shift+Enter for new line)'
                  }
                  disabled={!selectedEvent || (!useRAG && (!selectedUser || !conversationStarted)) || loading}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || !selectedEvent || (!useRAG && (!selectedUser || !conversationStarted)) || loading}
                  className="px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
                  style={{ height: '60px' }}
                >
                  <Send className="w-4 h-4" />
                  Send
                </button>
              </div>
            </>
          )}
        </div>

        {/* Session Info Panel (only in regular mode) */}
        {currentSession && !useRAG && (
          <div className="w-96 bg-white border border-gray-200 rounded-lg p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-800">Session Information</h3>
              <button
                onClick={() => setInspectionVisible(true)}
                className="flex items-center gap-1 px-2 py-1 border border-gray-300 rounded text-xs text-gray-600 hover:bg-gray-50"
              >
                <Eye className="w-3.5 h-3.5" />
                Inspect
              </button>
            </div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="py-2 font-medium text-gray-600 w-36">Session ID</td>
                  <td className="py-2 text-gray-900"><code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{currentSession.sessionId.substring(0, 16)}...</code></td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">User ID</td>
                  <td className="py-2 text-gray-900">{currentSession.userId}</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Knowledge Assistant</td>
                  <td className="py-2 text-gray-900">{events.find(e => e._id === selectedEvent)?.name}</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Conversations</td>
                  <td className="py-2 text-gray-900">{currentSession.conversations.length}</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Documents</td>
                  <td className="py-2 text-gray-900">{currentSession.documentData.length}</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Guardrails</td>
                  <td className="py-2 text-gray-900">{currentSession.guardrails.filter(g => g.isActive).length} active</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Behavioral Prompts</td>
                  <td className="py-2 text-gray-900">{currentSession.behavioralPrompts.filter(p => p.isActive).length} active</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Created</td>
                  <td className="py-2 text-gray-900">{new Date(currentSession.createdAt).toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* RAG Info Panel (only in RAG mode) */}
        {useRAG && selectedEvent && (
          <div className="w-96 bg-white border border-gray-200 rounded-lg p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                RAG Mode Active
              </h3>
              <button
                onClick={() => setInspectionVisible(true)}
                disabled={!lastPromptData}
                className="flex items-center gap-1 px-2 py-1 border border-gray-300 rounded text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Eye className="w-3.5 h-3.5" />
                Inspect
              </button>
            </div>
            <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md mb-4">
              <Info className="w-4 h-4 text-green-700 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800">Vector Search Enabled</p>
                <p className="text-xs text-green-700 mt-0.5">Searching document chunks by semantic similarity</p>
              </div>
            </div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="py-2 font-medium text-gray-600 w-36">Mode</td>
                  <td className="py-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      RAG (Retrieval Augmented Generation)
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Knowledge Assistant</td>
                  <td className="py-2 text-gray-900">{events.find(e => e._id === selectedEvent)?.name}</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Top K Chunks</td>
                  <td className="py-2 text-gray-900">{ragTopK}</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Messages</td>
                  <td className="py-2 text-gray-900">{messages.length}</td>
                </tr>
                {lastPromptData?.metadata && (
                  <>
                    <tr>
                      <td className="py-2 font-medium text-gray-600">Last Response</td>
                      <td className="py-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {lastPromptData.metadata.chunksUsed?.length || 0} chunks used
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 font-medium text-gray-600">Processing Time</td>
                      <td className="py-2 text-gray-900">{lastPromptData.metadata.processingTime}ms</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-medium text-gray-600">Tokens Used</td>
                      <td className="py-2 text-gray-900">{lastPromptData.metadata.tokensUsed || 'N/A'}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
            <p className="mt-4 text-xs text-gray-500">
              RAG mode retrieves only relevant chunks, not full documents
            </p>
          </div>
        )}
      </div>

      {/* Prompt Inspection Modal */}
      <Modal
        open={inspectionVisible}
        onClose={() => setInspectionVisible(false)}
        title={
          <>
            <Info className="w-4 h-4" />
            Prompt &amp; Session Data Inspection
          </>
        }
        wide
      >
        {renderPromptInspection()}
      </Modal>

      {/* Notifications Modal */}
      <Modal
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
        title={
          <>
            <Bell className="w-4 h-4" />
            Knowledge Assistant Notifications
          </>
        }
      >
        {selectedEvent ? (
          <div className="divide-y divide-gray-100">
            {notifications.length === 0 ? (
              <p className="py-4 text-sm text-gray-500 text-center">No notifications yet</p>
            ) : (
              notifications.map(n => (
                <div key={n._id} className="py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        n.priority === 'critical'
                          ? 'bg-red-100 text-red-700'
                          : n.priority === 'warning'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {n.priority.toUpperCase()}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{n.title}</span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{n.body}</p>
                  <p className="text-xs text-gray-400 mt-1">{new Date(n.createdAt).toLocaleString()}</p>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            Select a Knowledge Assistant to view notifications
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ChatTest;
